import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Search,
  Eye,
  Edit2,
  X,
  ChevronsLeft,
  ChevronsRight,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Plus,
  Users,
  CalendarClock,
  Briefcase,
  LogOut,
  ClipboardCheck,
  Share2,
  Download,
  UploadCloud,
  FileText,
} from "lucide-react";
import { useResumes } from "../../context/ResumeContext";
import { useInterviews } from "../../context/InterviewContext";
import { useOnboardings } from "../../context/OnboardingContext";
import { useOffboardings } from "../../context/Offboardingcontext";
import { useRecruitmentDocuments } from "../../context/Recruitmentdocumentcontext";
import { useClearances } from "../../context/ClearanceContext";
import { useDepartments } from "../../context/DepartmentContext";
import { useEmployees } from "../../context/EmployeeContext";
import { CDN_URL } from "../../config/apiConfig";

const STATUS_COLORS = {
  // greens
  hired: "bg-green-100 text-green-600",
  completed: "bg-green-100 text-green-600",
  approved: "bg-green-100 text-green-600",
  signed: "bg-green-100 text-green-600",
  cleared: "bg-green-100 text-green-600",
  // blues
  shortlisted: "bg-blue-100 text-blue-600",
  scheduled: "bg-blue-100 text-blue-600",
  viewed: "bg-blue-100 text-blue-600",
  "in progress": "bg-blue-100 text-blue-600",
  // yellows
  applied: "bg-yellow-100 text-yellow-600",
  pending: "bg-yellow-100 text-yellow-600",
  sent: "bg-yellow-100 text-yellow-600",
  initiated: "bg-yellow-100 text-yellow-600",
  // reds
  rejected: "bg-red-100 text-red-600",
  cancelled: "bg-red-100 text-red-600",
  "on hold": "bg-red-100 text-red-600",
  // purples
  interview: "bg-purple-100 text-purple-600",
  "interview scheduled": "bg-purple-100 text-purple-600",
};

const statusClass = (status) =>
  STATUS_COLORS[String(status || "").toLowerCase()] ||
  "bg-gray-100 text-gray-600";

const GRADIENT_BTN = "linear-gradient(135deg, #2563eb, #d12465)";

// ---------------------------------------------------------------------------
// Small shared building blocks (mirroring ReadOnlyField in Leaves.jsx)
// ---------------------------------------------------------------------------
const ReadOnlyField = ({ label, value }) => (
  <div>
    <label className="text-sm font-medium text-gray-600">{label}</label>
    <div className="w-full border border-gray-300 rounded-md px-3 py-2 mt-1 bg-gray-50 text-sm text-gray-700 break-words">
      {value || value === 0 ? value : "—"}
    </div>
  </div>
);

const StatusPill = ({ status }) => (
  <span
    className={`inline-block px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap ${statusClass(
      status
    )}`}
  >
    {status || "—"}
  </span>
);

const Field = ({ label, children, wide }) => (
  <div className={`min-w-0 ${wide ? "sm:col-span-2" : ""}`}>
    <label className="text-sm font-medium text-gray-600">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const inputCls =
  "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40";

const CLEARANCE_STATUS_OPTIONS = ["Pending", "Cleared", "Rejected"];

// Searchable employee picker used by the Clearance form — type to filter,
// click a result to select. Closes on outside click.
function EmployeeSearchSelect({ options, value, onSelect, placeholder = "Search employee..." }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);

  const selected = options.find((o) => o.id === value);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = options.filter((o) =>
    `${o.name} ${o.department || ""}`.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <input
          type="text"
          className={`${inputCls} pr-8`}
          placeholder={placeholder}
          value={open ? query : selected?.name || ""}
          onFocus={() => {
            setOpen(true);
            setQuery("");
          }}
          onChange={(e) => setQuery(e.target.value)}
        />
        <ChevronDown className="h-4 w-4 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
      </div>

      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto bg-white border rounded-md shadow-lg">
          {filtered.length ? (
            filtered.map((o) => (
              <button
                type="button"
                key={o.id}
                onClick={() => {
                  onSelect(o);
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-sky-50 flex items-center justify-between gap-2"
              >
                <span>{o.name}</span>
                <span className="text-sm text-gray-400">{o.department}</span>
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-gray-400">No matches</div>
          )}
        </div>
      )}
    </div>
  );
}

// Reads a picked File into a data URL so it can be previewed entirely
// in-browser before it's uploaded (used for local-only mock modules, and
// for instant preview on API-backed modules before the request completes).
const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Demo-only: builds a tiny placeholder file so the seeded sample rows have
// something real to view/download out of the box, without shipping actual
// binary assets. Real uploads (via the file inputs) replace this.
const demoFile = (name, label) => ({
  name,
  dataUrl:
    "data:text/plain;charset=utf-8," +
    encodeURIComponent(`${label}\n\nThis is a placeholder file for preview/download purposes.`),
});

// A file value can arrive in three shapes depending on where it came from:
//  - a plain filename string (e.g. `resume` straight from the API)
//  - a local demo object { name, dataUrl }
//  - a freshly picked object { name, dataUrl, file } (raw File attached,
//    ready to be sent to the API as multipart/form-data)
// This normalizes all three into a single { name, url } shape for display.
const normalizeFileValue = (val) => {
  if (!val) return null;
  if (typeof val === "string") {
    return { name: val, url: `${CDN_URL}/${val}` };
  }
  return { name: val.name, url: val.dataUrl || val.url };
};

// ---------------------------------------------------------------------------
// Module configuration — one entry per tab. Add/edit column defs here to
// extend a module without touching the generic table/modal rendering below.
// ---------------------------------------------------------------------------
const todayOffset = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
};

const MODULES = {
 candidates: {label: "Candidates", icon: Users, idPrefix: "CND",
  searchKeys: ["name", "email", "phone", "position", "status",],
  statusOptions: ["Applied", "Shortlisted", "Interview", "Selected", "Rejected", "Hired",],
  columns: [
    { key: "name", label: "Candidate Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "position", label: "Position" },
    { key: "experience", label: "Experience" },
    { key: "present_ctc", label: "Present CTC" },
    { key: "expected_ctc", label: "Expected CTC" },
    { key: "notice_period", label: "Notice Period" },
    { key: "resume", label: "Resume", type: "file" },
    { key: "status", label: "Status", type: "status" },
  ],

  // NOTE: every one of these keys is sent to the API on Add/Edit (see
  // buildResumeFormData in ModuleTable) — present_ctc, expected_ctc and
  // notice_period are included, matching the resumes table columns.
  formFields: [
    {key: "name", label: "Candidate Name", type: "text", required: true,},
    {key: "email", label: "Email", type: "text", required: true,},
    {key: "phone", label: "Phone", type: "text", required: true,},
    {key: "position", label: "Position Applied", type: "text", required: true,},
    {key: "experience", label: "Experience", type: "text", placeholder: "Ex: 3 Years",},
    {key: "present_ctc", label: "Present CTC", type: "text", placeholder: "Ex: 5 LPA",},
    {key: "expected_ctc", label: "Expected CTC", type: "text", placeholder: "Ex: 7 LPA",},
    {key: "notice_period", label: "Notice Period", type: "text", placeholder: "Ex: 30 Days",},
    {key: "resume", label: "Resume", type: "file", accept: ".pdf,.doc,.docx", required: true,},
    {key: "status", label: "Status", type: "select",
      options: ["Applied", "Shortlisted", "Interview", "Rejected",],
    },
    {key: "notes", label: "Notes", type: "textarea", },
  ],
  seed: [],
  },

  interviews: {
    label: "Interviews", icon: CalendarClock, idPrefix: "INT",
    searchKeys: ["candidate", "position", "interviewer"],
    statusOptions: ["Scheduled", "In Progress", "Completed", "Cancelled"],
    columns: [
      { key: "name", label: "Candidate" },
      // { key: "position", label: "Position" },
      { key: "interviewer", label: "Interviewer" },
      { key: "date", label: "Date" },
      { key: "time", label: "Time" },
      { key: "mode", label: "Mode" },
      { key: "status", label: "Status", type: "status" },
    ],
    formFields: [
      { key: "candidate", label: "Candidate", type: "candidate-select", required: true },
      // { key: "position", label: "Position", type: "text" },
      { key: "interviewer", label: "Interviewer", type: "text", required: true },
      { key: "round", label: "Round", type: "select", options: ["Screening", "Technical", "Managerial", "HR"] },
      { key: "date", label: "Date", type: "date", required: true },
      { key: "time", label: "Time", type: "time" },
      { key: "mode", label: "Mode", type: "select", options: ["Online", "Onsite", "Phone"] },
      { key: "status", label: "Status", type: "select", options: ["Scheduled", "In Progress", "Completed", "Cancelled"] },
    ],
    seed: [],
  },

 onboarding: {
  label: "Onboarding",
  icon: Briefcase,
  idPrefix: "ONB",
  searchKeys: ["employee_code", "department", "designation"],
  statusOptions: ["Pending", "In Progress", "Completed"],
  columns: [
    { key: "candidate_name", label: "Candidate" },
    { key: "employee_code", label: "Employee Code" },
    { key: "department", label: "Department" },
    { key: "designation", label: "Designation" },
    { key: "joining_date", label: "Joining Date" },
    { key: "status", label: "Status", type: "status" },
  ],
  formFields: [
    { key: "resume_id", label: "Candidate", type: "resume-select", required: true },
    { key: "employee_code", label: "Employee Code", type: "text", required: true, placeholder: "EMP001" },
    { key: "joining_date", label: "Joining Date", type: "date", required: true },
    { key: "department", label: "Department", type: "department-select", valueField: "name", required: true },
    { key: "designation", label: "Designation", type: "text", required: true },
  ],
  seed: [],
},

  offboarding: {
    label: "Offboarding",
    icon: LogOut,
    idPrefix: "OFB",
    searchKeys: ["name", "department", "reason"],
    statusOptions: ["Initiated", "In Progress", "Completed"],
    columns: [
      { key: "name", label: "Employee Name" },
      { key: "department", label: "Department" },
      { key: "last_working_day", label: "Last Working Day" },
      { key: "reason", label: "Reason" },
      { key: "status", label: "Status", type: "status" },
    ],
    formFields: [
      { key: "user_id", label: "Employee Name", type: "employee-select", sourceModule: "employees", required: true },
      { key: "department", label: "Department", type: "department-select", valueField: "name" },
      { key: "resignation_date", label: "Resignation Date", type: "date" },
      { key: "last_working_day", label: "Last Working Day", type: "date", required: true },
      { key: "reason", label: "Reason", type: "select", options: ["Resignation", "Termination", "Retirement", "Contract End"] },
      { key: "status", label: "Status", type: "select", options: ["Initiated", "In Progress", "Completed"] },
      { key: "notes", label: "Exit Notes", type: "textarea" },
    ],
    seed: [],
  },

  clearance: {
    label: "Clearance",
    icon: ClipboardCheck,
    idPrefix: "CLR",
    searchKeys: ["name", "department_name"],
    statusOptions: ["Pending", "In Progress", "Cleared", "Rejected"],
    columns: [
      { key: "name", label: "Employee Name" },
      { key: "department_name", label: "Department" },
      { key: "it_clearance", label: "IT", type: "status" },
      { key: "finance_clearance", label: "Finance", type: "status" },
      { key: "hr_clearance", label: "HR", type: "status" },
      { key: "admin_clearance", label: "Admin", type: "status" },
      { key: "status", label: "Overall Status", type: "status" },
    ],
    // Employee options are pulled live from Candidates — the backend FK
    // (`clearance_process.resume_id`) points at `resumes`, so the picker
    // must resolve to a real resume id, not an Offboarding row id.
    formFields: [
      { key: "employee_id", label: "Employee", type: "employee-select", sourceModule: "candidates", required: true },
      { key: "department", label: "Department", type: "department-select", required: true },
      { key: "it_clearance", label: "IT Clearance", type: "select", options: CLEARANCE_STATUS_OPTIONS },
      { key: "finance_clearance", label: "Finance Clearance", type: "select", options: CLEARANCE_STATUS_OPTIONS },
      { key: "hr_clearance", label: "HR Clearance", type: "select", options: CLEARANCE_STATUS_OPTIONS },
      { key: "admin_clearance", label: "Admin Clearance (optional)", type: "select", options: CLEARANCE_STATUS_OPTIONS },
      { key: "status", label: "Overall Status (auto-calculated)", type: "computed" },
      { key: "remarks", label: "Remarks", type: "textarea" },
    ],
    // Recomputes Overall Status every time a clearance field changes:
    // any "Rejected" wins, all-Cleared (Admin counts only if set) -> Cleared,
    // any progress -> In Progress, otherwise Pending.
    computeDerived: (v) => {
      const required = [v.it_clearance, v.finance_clearance, v.hr_clearance];
      const withAdmin = v.admin_clearance ? [...required, v.admin_clearance] : required;
      let status = "Pending";
      if (withAdmin.includes("Rejected")) {
        status = "Rejected";
      } else if (
        required.every((s) => s === "Cleared") &&
        (!v.admin_clearance || v.admin_clearance === "Cleared")
      ) {
        status = "Cleared";
      } else if (withAdmin.some(Boolean)) {
        status = "In Progress";
      }
      return { status };
    },
    // 🔌 Now API-backed via ClearanceContext — local seed no longer used
    // as the source of truth, kept empty so nothing stale shows before
    // the live fetch resolves.
    seed: [],
  },

  documents: {
    label: "Documents",
    icon: Share2,
    idPrefix: "DOC",
    searchKeys: ["doc_name", "shared_with"],
    statusOptions: ["Sent", "Viewed", "Signed"],
    columns: [
      { key: "doc_name", label: "Document" },
      { key: "related_to", label: "Related To" },
      { key: "shared_with", label: "Shared With" },
      { key: "shared_date", label: "Shared Date" },
      { key: "file", label: "Uploaded File", type: "file" },
      { key: "status", label: "Status", type: "status" },
    ],
    formFields: [
      { key: "doc_name", label: "Document Name", type: "text", required: true, placeholder: "Offer Letter.pdf" },
      { key: "related_to", label: "Related To", type: "text", placeholder: "Candidate / Employee name" },
      { key: "shared_with", label: "Shared With", type: "text", required: true },
      { key: "shared_date", label: "Shared Date", type: "date", required: true },
      { key: "file", label: "Attach File", type: "file", accept: ".pdf,.doc,.docx" },
      { key: "status", label: "Status", type: "select", options: ["Sent", "Viewed", "Signed"] },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
    seed: [],
  },
};

// ---------------------------------------------------------------------------
// Generic Add / View-Edit modal, config-driven from a module's formFields
// ---------------------------------------------------------------------------
function RecordFormFields({ fields, values, onChange, disabled, layout = "stack", allData }) {
  const [uploadingKey, setUploadingKey] = useState(null);

  const handleFilePick = async (key, fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    setUploadingKey(key);
    try {
      const dataUrl = await fileToDataUrl(file);
      // Keep the raw File object too — needed to build the multipart
      // FormData payload when this field belongs to an API-backed module
      // (e.g. Candidates -> `resume`).
      onChange(key, { name: file.name, dataUrl, file });
    } finally {
      setUploadingKey(null);
    }
  };

  const wrapperCls =
    layout === "grid"
      ? "grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3"
      : "space-y-3";

  return (
    <div className={wrapperCls}>
      {fields.map((f) => {
        if (f.type === "candidate-select") {
  const candidates = allData?.candidates || [];

  return (
    <Field key={f.key} label={f.label}>
      <select
        className={inputCls}
        value={values[f.key] ?? ""}
        disabled={disabled}
        onChange={(e) => {
          const selected = candidates.find(
            (c) => String(c.id) === e.target.value
          );

          onChange("candidate_id", selected?.id || "");
          onChange("candidate", selected?.name || "");
          onChange("position", selected?.position || "");
        }}
      >
        <option value="">Select Candidate</option>

        {candidates.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
    </Field>
  );
}
        if (f.type === "resume-select") {
          const candidates = allData?.candidates || [];

          return (
            <Field key={f.key} label={f.label}>
              <select
                className={inputCls}
                value={values[f.key] ?? ""}
                disabled={disabled}
                onChange={(e) => {
                  const selected = candidates.find(
                    (c) => String(c.id) === e.target.value
                  );

                  onChange("resume_id", selected?.id || "");
                  onChange("candidate_name", selected?.name || "");
                }}
              >
                <option value="">Select Candidate</option>

                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          );
        }
        if (f.type === "employee-select") {
          const options = (allData?.[f.sourceModule] || []).map((r) => ({
            id: r.id,
            name: r.name,
            department: r.department,
          }));
          return (
            <Field key={f.key} label={f.label} wide>
              <EmployeeSearchSelect
                options={options}
                value={values[f.key]}
                onSelect={(emp) => {
                  onChange(f.key, emp.id);
                  onChange("name", emp.name);
                  if (emp.department) onChange("department", emp.department);
                  if (f.sourceModule === "candidates") {
                    // Clearance's FK needs the real resume id — options here
                    // come from Candidates, so the option id already is it.
                    onChange("resume_id", emp.id);
                  }
                }}
              />
            </Field>
          );
        }
        if (f.type === "department-select") {
          const departments = allData?.departments || [];
          const valueKey = f.valueField || "id";

          return (
            <Field key={f.key} label={f.label}>
              <select
                className={inputCls}
                value={values[f.key] ?? ""}
                disabled={disabled}
                onChange={(e) => onChange(f.key, e.target.value)}
              >
                <option value="">Select Department</option>

                {departments.map((d) => (
                  <option key={d.id} value={d[valueKey]}>
                    {d.name}
                  </option>
                ))}
              </select>
            </Field>
          );
        }
        if (f.type === "computed") {
          return (
            <Field key={f.key} label={f.label} wide>
              <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50">
                <StatusPill status={values[f.key]} />
              </div>
            </Field>
          );
        }
        if (f.readOnly) {
          return (
            <Field key={f.key} label={f.label}>
              <div className={`${inputCls} bg-gray-50 text-gray-600`}>
                {values[f.key] || <span className="text-gray-400">{f.placeholder || "Auto-filled"}</span>}
              </div>
            </Field>
          );
        }
        if (f.type === "file") {
          const current = values[f.key];
          const currentDisplay = normalizeFileValue(current);
          return (
            <Field key={f.key} label={f.label} wide>
              <div className="flex flex-wrap items-center gap-2">
                <label
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-gray-300 text-sm cursor-pointer hover:bg-gray-50 ${
                    disabled ? "opacity-50 pointer-events-none" : ""
                  }`}
                >
                  <UploadCloud className="h-4 w-4" />
                  {uploadingKey === f.key ? "Uploading..." : currentDisplay ? "Replace file" : "Choose file"}
                  <input
                    type="file"
                    accept={f.accept}
                    className="hidden"
                    disabled={disabled}
                    onChange={(e) => handleFilePick(f.key, e.target.files)}
                  />
                </label>

                {currentDisplay ? (
                  <a
                    href={currentDisplay.url}
                    download={currentDisplay.name}
                    target={currentDisplay.url?.startsWith("data:") ? undefined : "_blank"}
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-green-50 text-green-700 text-sm hover:bg-green-100 max-w-[220px]"
                    title={currentDisplay.name}
                  >
                    <FileText className="h-4 w-4 shrink-0" />
                    <span className="truncate">{currentDisplay.name}</span>
                  </a>
                ) : (
                  <span className="text-sm text-gray-400">No file uploaded</span>
                )}
              </div>
            </Field>
          );
        }
        if (f.type === "textarea") {
          return (
            <Field key={f.key} label={f.label} wide>
              <textarea
                rows={3}
                className={inputCls}
                value={values[f.key] ?? ""}
                disabled={disabled}
                onChange={(e) => onChange(f.key, e.target.value)}
              />
            </Field>
          );
        }
        if (f.type === "select") {
          return (
            <Field key={f.key} label={f.label}>
              <select
                className={inputCls}
                value={values[f.key] ?? ""}
                disabled={disabled}
                onChange={(e) => onChange(f.key, e.target.value)}
              >
                <option value="">Select {f.label}</option>
                {f.options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </Field>
          );
        }
        return (
          <Field key={f.key} label={f.label}>
            <input
              type={f.type === "date" ? "date" : f.type === "time" ? "time" : "text"}
              className={inputCls}
              placeholder={f.placeholder || ""}
              value={values[f.key] ?? ""}
              disabled={disabled}
              min={f.type === "date" ? todayOffset(0) : undefined}
              onChange={(e) => {
                const val = e.target.value;
                if (f.type === "date" && val && val < todayOffset(0)) return;
                onChange(f.key, val);
              }}
            />
          </Field>
        );
      })}
    </div>
  );
}

function AddModal({ moduleDef, onClose, onSave, allData, externalError, submitting }) {
  const [values, setValues] = useState(() =>
    moduleDef.computeDerived ? moduleDef.computeDerived({}) : {}
  );
  const [error, setError] = useState("");

  const update = (key, val) =>
    setValues((prev) => {
      let next = { ...prev, [key]: val };
      if (moduleDef.computeDerived) next = { ...next, ...moduleDef.computeDerived(next) };
      return next;
    });

  const handleSave = () => {
    const missing = moduleDef.formFields.filter((f) => f.required && !values[f.key]);
    if (missing.length) {
      setError(`Please fill: ${missing.map((f) => f.label).join(", ")}`);
      return;
    }
    setError("");
    onSave(values);
  };

  // Group into two-column rows for compactness on wider screens, but stack on mobile
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-3 sm:px-4">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl relative max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-4 sm:px-6 pt-4 sm:pt-5 pb-3 border-b shrink-0">
          <h2 className="text-lg font-semibold pr-6">
            Add {moduleDef.label.replace(/s$/, "")}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-4 sm:px-6 py-4">
          <RecordFormFields
            fields={moduleDef.formFields}
            values={values}
            onChange={update}
            layout="grid"
            allData={allData}
          />

          {(error || externalError) && (
            <div className="text-red-500 text-sm mt-3">{error || externalError}</div>
          )}
        </div>

        <div className="px-4 sm:px-6 py-3 border-t shrink-0 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:space-x-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-60 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={submitting}
            className="px-4 py-2 rounded-md text-white disabled:opacity-60 text-sm"
            style={{ background: GRADIENT_BTN }}
          >
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ViewEditModal({ moduleDef, record, isEditMode, onClose, onSave, allData }) {
  const [values, setValues] = useState(record);
  const update = (key, val) =>
    setValues((prev) => {
      let next = { ...prev, [key]: val };
      if (moduleDef.computeDerived) next = { ...next, ...moduleDef.computeDerived(next) };
      return next;
    });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-3 sm:px-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg relative max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-4 sm:px-5 pt-4 sm:pt-5 pb-3 border-b shrink-0">
          <h2 className="text-lg font-semibold pr-6">
            {isEditMode ? `Edit ${moduleDef.label.replace(/s$/, "")}` : `View ${moduleDef.label.replace(/s$/, "")}`}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-4 sm:px-5 py-4 space-y-3">
          {isEditMode ? (
            <RecordFormFields
              fields={moduleDef.formFields}
              values={values}
              onChange={update}
              allData={allData}
            />
          ) : (
            moduleDef.formFields.map((f) => {
              if (f.type === "file") {
                const current = values[f.key];
                return (
                  <div key={f.key}>
                    <label className="text-sm font-medium text-gray-600">{f.label}</label>
                    <div className="w-full border border-gray-300 rounded-md px-3 py-2 mt-1 bg-gray-50">
                      {current ? (
                        <div className="flex items-center justify-between gap-2">
                          <span className="inline-flex items-center gap-1.5 text-sm text-gray-700 truncate">
                            <FileText className="h-4 w-4 shrink-0 text-sky-600" />
                            <span className="truncate">{current.name}</span>
                          </span>
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              type="button"
                              onClick={() => window.open(current.dataUrl, "_blank")}
                              className="text-sky-600 hover:text-sky-800"
                              title="View"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <a
                              href={current.dataUrl}
                              download={current.name}
                              className="text-green-600 hover:text-green-800"
                              title="Download"
                            >
                              <Download className="h-4 w-4" />
                            </a>
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">Not uploaded</span>
                      )}
                    </div>
                  </div>
                );
              }
              if (f.key === "status" || f.type === "computed") {
                return (
                  <div key={f.key}>
                    <label className="text-sm font-medium text-gray-600">{f.label}</label>
                    <div className="mt-1">
                      <StatusPill status={values[f.key]} />
                    </div>
                  </div>
                );
              }
              if (f.type === "employee-select") {
                return <ReadOnlyField key={f.key} label={f.label} value={values.name} />;
              }
              if (f.type === "candidate-select") {
                return (
                  <ReadOnlyField
                    key={f.key}
                    label={f.label}
                    value={values.name}
                  />
                );
              }
              if (f.type === "resume-select") {
                return (
                  <ReadOnlyField
                    key={f.key}
                    label={f.label}
                    value={values.candidate_name}
                  />
                );
              }
              if (f.type === "department-select") {
                const dept = (allData?.departments || []).find(
                  (d) =>
                    String(d.id) === String(values[f.key]) ||
                    d.name === values[f.key]
                );
                return (
                  <ReadOnlyField
                    key={f.key}
                    label={f.label}
                    value={dept?.name || values[f.key]}
                  />
                );
              }
              if (f.type === "select") {
                return (
                  <div key={f.key}>
                    <label className="text-sm font-medium text-gray-600">{f.label}</label>
                    <div className="mt-1">
                      <ReadOnlyField label="" value={values[f.key]} />
                    </div>
                  </div>
                );
              }
              return <ReadOnlyField key={f.key} label={f.label} value={values[f.key]} />;
            })
          )}
        </div>

        {isEditMode && (
          <div className="px-4 sm:px-5 py-3 border-t shrink-0 flex justify-end">
            <button
              onClick={() => onSave(values)}
              className="text-white px-4 py-2 rounded-md w-full sm:w-auto text-sm"
              style={{ background: GRADIENT_BTN }}
            >
              Save
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generic module table (mirrors the Leaves table structure/pagination)
// ---------------------------------------------------------------------------
function ModuleTable({ moduleKey, moduleDef, records, setRecords, allData }) {
  const isResumeModule = moduleKey === "candidates";
const isInterviewModule = moduleKey === "interviews";
const isOnboardingModule = moduleKey === "onboarding";
const isOffboardingModule = moduleKey === "offboarding";
const isDocumentModule = moduleKey === "documents";
const isClearanceModule = moduleKey === "clearance";

const isApiBacked =
  isResumeModule ||
  isInterviewModule ||
  isOnboardingModule ||
  isOffboardingModule ||
  isDocumentModule ||
  isClearanceModule;

  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

  const auditUserId =
    currentUser?.id ||
    currentUser?.user_id ||
    "";

  // 🔌 CONNECT: this is the live wiring for Candidates. When another module
  // gets its own API, pull its context here the same way and branch on
  // moduleKey below (or extract this into a small per-module adapter).
  const {resumes, loading: resumesLoading, error: resumesError, createResume, updateResume} = useResumes();
  const {interviews, loading: interviewsLoading, error: interviewsError, scheduleInterview, updateInterview} = useInterviews();
  const {onboardings, loading: onboardingsLoading, error: onboardingsError, createOnboarding, updateOnboarding} = useOnboardings();
  const {offboardings, loading: offboardingsLoading, error: offboardingsError, createOffboarding, updateOffboarding} = useOffboardings();
  const {recruitmentDocuments, loading: documentsLoading, error: documentsError, createRecruitmentDocument, updateRecruitmentDocument} = useRecruitmentDocuments();
  const {clearances, loading: clearancesLoading, error: clearancesError, createClearance, updateClearance} = useClearances();

  const sourceRecords = isResumeModule
    ? resumes
    : isInterviewModule
    ? interviews
    : isOnboardingModule
    ? onboardings
    : isOffboardingModule
    ? offboardings
    : isDocumentModule
    ? recruitmentDocuments
    : isClearanceModule
    ? clearances.map((c) => {
        // 🔌 Backend record keys the employee picker by `resume_id` (FK ->
        // resumes) and `department` by a department id — bridge those to
        // the form's field keys (`employee_id`, `department`) without
        // touching the form config, and resolve display-only labels
        // (`department_name` already comes resolved from the API; fall
        // back to the live Departments list if it's ever missing).
        const dept = (allData?.departments || []).find(
          (d) => String(d.id) === String(c.department)
        );
        const rawStatus = String(c.clearance_status ?? c.status ?? "").trim();
        const displayStatus = rawStatus
          ? rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase()
          : "";
        return {
          ...c,
          employee_id: c.employee_id || c.resume_id,
          name: c.name || "",
          department: c.department || "",
          department_name: c.department_name || dept?.name || c.department || "",
          status: displayStatus,
        };
      })
    : records;

  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");

  const filtered = useMemo(() => {
    return (sourceRecords || []).filter((rec) => {
      const matchesStatus = filter === "All" || String(rec.status || "").toLowerCase() === filter.toLowerCase();
      const searchLower = search.toLowerCase();
      const matchesSearch =
        !searchLower ||
        moduleDef.searchKeys.some((k) => String(rec[k] || "").toLowerCase().includes(searchLower));
      return matchesStatus && matchesSearch;
    });
  }, [sourceRecords, filter, search, moduleDef.searchKeys]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filtered.slice(indexOfFirstRow, indexOfLastRow);
  const totalColumns = moduleDef.columns.length + 1; // +1 for Actions

  // Quick inline upload straight from the row, without opening the modal —
  // mirrors the upload icon in the Letters/Documents table.
  const fileColumn = moduleDef.columns.find((c) => c.type === "file");
  const fileInputRef = useRef(null);
  const uploadTargetId = useRef(null);

  const triggerUpload = (id) => {
    uploadTargetId.current = id;
    fileInputRef.current?.click();
  };

  const handleInlineFileChange = async (e) => {
    const file = e.target.files?.[0];
    const id = uploadTargetId.current;
    e.target.value = ""; // allow re-selecting the same file later
    if (!file || id == null || !fileColumn) return;

    if (isResumeModule) {
      const fd = new FormData();
      fd.append("resume", file);
      await updateResume(id, fd);
      return;
    }

    if (isDocumentModule) {
      const dataUrl = await fileToDataUrl(file);
      await updateRecruitmentDocument(id, {
        [fileColumn.key]: { name: file.name, dataUrl },
      });
      return;
    }

    // 🔌 CONNECT: replace with your actual file-upload API call for this
    // module, then store the returned URL instead of a data URL.
    const dataUrl = await fileToDataUrl(file);
    setRecords((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [fileColumn.key]: { name: file.name, dataUrl } } : r))
    );
  };

  // Builds the multipart FormData payload for Candidates: every formField
  // is included so present_ctc / expected_ctc / notice_period always reach
  // the API, and the file field is only appended when a new file was picked
  // (so editing without replacing the resume leaves resume untouched).
  const buildResumeFormData = (values) => {
    const fd = new FormData();
    moduleDef.formFields.forEach((f) => {
      if (f.type === "file") {
        const fileVal = values[f.key];
        if (fileVal?.file) fd.append(f.key, fileVal.file);
      } else {
        fd.append(f.key, values[f.key] ?? "");
      }
    });
    return fd;
  };

const handleAdd = async (values) => {
  if (isResumeModule) {
    setSubmitting(true);
    setApiError("");

    const formData = buildResumeFormData(values);
    formData.append("create_audit_id", auditUserId);

    const result = await createResume(formData);

    setSubmitting(false);

    if (!result?.success) {
      setApiError(result?.message || "Failed to add candidate");
      return;
    }

    setShowAddModal(false);
    return;
  }

  if (isInterviewModule) {
    setSubmitting(true);
    setApiError("");

    const payload = {
      ...values,
      create_audit_id: auditUserId,
    };

    const result = await scheduleInterview(payload);

    setSubmitting(false);

    if (!result?.success) {
      setApiError(result?.message || "Failed to schedule interview");
      return;
    }

    setShowAddModal(false);
    return;
  }

  if (isOnboardingModule) {
    setSubmitting(true);
    setApiError("");

    const user = JSON.parse(localStorage.getItem("user") || "{}");

    const payload = {
      ...values,
      create_audit_id: user.id,
    };

    const result = await createOnboarding(payload);

    setSubmitting(false);

    if (!result.success) {
      setApiError(result.message || "Failed to create onboarding");
      return;
    }

    setShowAddModal(false);
    return;
  }

  if (isOffboardingModule) {
    setSubmitting(true);
    setApiError("");

    const user = JSON.parse(localStorage.getItem("user") || "{}");

    const payload = {
      ...values,
      create_audit_id: user.id,
    };

    const result = await createOffboarding(payload);

    setSubmitting(false);

    if (!result?.success) {
      setApiError(result?.message || "Failed to create offboarding request");
      return;
    }

    setShowAddModal(false);
    return;
  }

  if (isDocumentModule) {
    setSubmitting(true);
    setApiError("");

    const user = JSON.parse(localStorage.getItem("user") || "{}");

    const payload = {
      ...values,
      create_audit_id: user.id,
    };

    const result = await createRecruitmentDocument(payload);

    setSubmitting(false);

    if (!result?.success) {
      setApiError(result?.message || "Failed to add document");
      return;
    }

    setShowAddModal(false);
    return;
  }

  if (isClearanceModule) {
    setSubmitting(true);
    setApiError("");

    const payload = {
      ...values,
      // Backend field is `clearance_status`, not `status` — send both so
      // the record actually persists against the real API contract.
      clearance_status: String(values.status || "").toLowerCase(),
      // Backend field is `resume_id`, not `employee_id` — the picker now
      // sets `resume_id` directly on select, fall back to `employee_id`
      // only if that's somehow missing.
      resume_id: values.resume_id || values.employee_id,
      create_audit_id: auditUserId,
    };

    const result = await createClearance(payload);

    setSubmitting(false);

    if (!result?.success) {
      setApiError(result?.message || "Failed to create clearance record");
      return;
    }

    setShowAddModal(false);
    return;
  }

  // Local modules
  const nextId = records.length
    ? Math.max(...records.map((r) => r.id)) + 1
    : 1;

  setRecords((prev) => [...prev, { id: nextId, ...values }]);
  setShowAddModal(false);
};

 const handleSaveEdit = async (values) => {
  if (isResumeModule) {
    setSubmitting(true);
    setApiError("");

    const formData = buildResumeFormData(values);
    formData.append("update_audit_id", auditUserId);

    const result = await updateResume(values.id, formData);

    setSubmitting(false);

    if (!result?.success) {
      setApiError(result?.message || "Failed to update candidate");
      return;
    }

    setSelected(null);
    return;
  }

  if (isInterviewModule) {
    setSubmitting(true);
    setApiError("");

    const payload = {
      ...values,
      update_audit_id: auditUserId,
    };

    const result = await updateInterview(values.id, payload);

    setSubmitting(false);

    if (!result?.success) {
      setApiError(result?.message || "Failed to update interview");
      return;
    }

    setSelected(null);
    return;
  }

  if (isOnboardingModule) {
    setSubmitting(true);
    setApiError("");

    const user = JSON.parse(localStorage.getItem("user") || "{}");

    const payload = {
      ...values,
      update_audit_id: user.id,
    };

    const result = await updateOnboarding(values.id, payload);

    setSubmitting(false);

    if (!result.success) {
      setApiError(result.message || "Failed to update onboarding");
      return;
    }

    setSelected(null);
    return;
  }

  if (isOffboardingModule) {
    setSubmitting(true);
    setApiError("");

    const user = JSON.parse(localStorage.getItem("user") || "{}");

    const payload = {
      ...values,
      update_audit_id: user.id,
    };

    const result = await updateOffboarding(values.id, payload);

    setSubmitting(false);

    if (!result?.success) {
      setApiError(result?.message || "Failed to update offboarding record");
      return;
    }

    setSelected(null);
    return;
  }

  if (isDocumentModule) {
    setSubmitting(true);
    setApiError("");

    const user = JSON.parse(localStorage.getItem("user") || "{}");

    const payload = {
      ...values,
      update_audit_id: user.id,
    };

    const result = await updateRecruitmentDocument(values.id, payload);

    setSubmitting(false);

    if (!result?.success) {
      setApiError(result?.message || "Failed to update document");
      return;
    }

    setSelected(null);
    return;
  }

  if (isClearanceModule) {
    setSubmitting(true);
    setApiError("");

    const payload = {
      ...values,
      // Backend field is `clearance_status`, not `status` — send both so
      // the record actually persists against the real API contract.
      clearance_status: String(values.status || "").toLowerCase(),
      // Backend field is `resume_id`, not `employee_id` — the picker now
      // sets `resume_id` directly on select, fall back to `employee_id`
      // only if that's somehow missing.
      resume_id: values.resume_id || values.employee_id,
      update_audit_id: auditUserId,
    };

    const result = await updateClearance(values.id, payload);

    setSubmitting(false);

    if (!result?.success) {
      setApiError(result?.message || "Failed to update clearance record");
      return;
    }

    setSelected(null);
    return;
  }

  // Local modules
  setRecords((prev) =>
    prev.map((r) => (r.id === values.id ? { ...r, ...values } : r))
  );

  setSelected(null);
};

  const goToPage = (p) => setCurrentPage(Math.min(Math.max(p, 1), totalPages));

  return (
    <>
      {/* Filter + Search */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 bg-white rounded-xl p-3 shadow-sm gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full lg:w-auto">
          <div className="flex flex-col xs:flex-row sm:flex-row items-stretch border border-gray-300 rounded-md overflow-hidden w-full sm:w-auto focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
            <select
              className="px-2 py-2 border-b sm:border-b-0 sm:border-r border-gray-300 text-sm w-full sm:w-auto"
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="All">All</option>
              {moduleDef.statusOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <div className="relative w-full sm:w-40 md:w-56">
              <Search className="absolute left-2 top-2.5 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-8 pr-2 py-2 w-full outline-none text-sm"
              />
            </div>
          </div>

          {(search || filter !== "All") && (
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setFilter("All");
                setCurrentPage(1);
              }}
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
            >
              <X size={14} />
              Clear Filters
            </button>
          )}
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="text-white px-4 py-2 rounded-md shrink-0 w-full lg:w-auto flex items-center justify-center gap-1 text-sm font-semibold"
          style={{ background: GRADIENT_BTN }}
        >
          <Plus className="h-4 w-4" /> Add {moduleDef.label.replace(/s$/, "")}
        </button>
      </div>

      {isResumeModule && resumesLoading && (
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4 text-sm">
          Loading candidates...
        </div>
      )}

      {isInterviewModule && interviewsLoading && (
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4 text-sm">
          Loading interviews...
        </div>
      )}

      {isOnboardingModule && onboardingsLoading && (
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4 text-sm">
          Loading onboarding records...
        </div>
      )}

      {isOffboardingModule && offboardingsLoading && (
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4 text-sm">
          Loading offboarding records...
        </div>
      )}

      {isDocumentModule && documentsLoading && (
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4 text-sm">
          Loading documents...
        </div>
      )}

      {isClearanceModule && clearancesLoading && (
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4 text-sm">
          Loading clearance records...
        </div>
      )}

      {isResumeModule && resumesError && (
        <div className="bg-red-50 text-red-600 rounded-xl shadow-sm p-4 mb-4 text-sm">
          {resumesError}
        </div>
      )}

      {isInterviewModule && interviewsError && (
        <div className="bg-red-50 text-red-600 rounded-xl shadow-sm p-4 mb-4 text-sm">
          {interviewsError}
        </div>
      )}

      {isOnboardingModule && onboardingsError && (
        <div className="bg-red-50 text-red-600 rounded-xl shadow-sm p-4 mb-4 text-sm">
          {onboardingsError}
        </div>
      )}

      {isOffboardingModule && offboardingsError && (
        <div className="bg-red-50 text-red-600 rounded-xl shadow-sm p-4 mb-4 text-sm">
          {offboardingsError}
        </div>
      )}

      {isDocumentModule && documentsError && (
        <div className="bg-red-50 text-red-600 rounded-xl shadow-sm p-4 mb-4 text-sm">
          {documentsError}
        </div>
      )}

      {isClearanceModule && clearancesError && (
        <div className="bg-red-50 text-red-600 rounded-xl shadow-sm p-4 mb-4 text-sm">
          {clearancesError}
        </div>
      )}

      {/* Table */}
      <div className="bg-white shadow rounded-xl p-3 sm:p-4">
        {fileColumn && (
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx"
            className="hidden"
            onChange={handleInlineFileChange}
          />
        )}
        <div className="overflow-x-auto -mx-3 sm:mx-0">
          <table className="min-w-[900px] sm:min-w-full border-collapse text-sm">
            <thead className="bg-sky-50">
              <tr>
                <th className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-600 whitespace-nowrap">
                  ID
                </th>
                {moduleDef.columns.map((col) => (
                  <th
                    key={col.key}
                    className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-600 whitespace-nowrap"
                  >
                    {col.label}
                  </th>
                ))}
                <th className="py-3 px-2 sm:px-4 text-center font-semibold text-gray-600 whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {currentRows.length > 0 ? (
                currentRows.map((rec, index) => (
                  <tr
                    key={rec.id}
                    className={`border-b hover:bg-gray-50 ${
                      index % 2 === 1 ? "bg-[#f7f6fe]" : "bg-white"
                    }`}
                  >
                    <td className="py-3 px-2 sm:px-4 text-[#8c57ff] whitespace-nowrap">
                      {`${moduleDef.idPrefix}-${(index + 1).toString().padStart(2, "0")}`}
                    </td>
                    {moduleDef.columns.map((col) => (
                      <td key={col.key} className="py-3 px-2 sm:px-4 whitespace-nowrap max-w-[200px]">
                        {col.type === "status" ? (
                          <StatusPill status={rec[col.key]} />
                        ) : col.type === "file" ? (
                          (() => {
                            const display = normalizeFileValue(rec[col.key]);
                            return display ? (
                              <div className="flex items-center gap-2">
                                <a
                                  href={display.url}
                                  download={display.name}
                                  target={display.url?.startsWith("data:") ? undefined : "_blank"}
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 rounded-md bg-green-50 text-green-700 px-2 py-1 text-sm font-medium hover:bg-green-100 max-w-[150px]"
                                  title={`Download ${display.name}`}
                                >
                                  <Download className="h-3 w-3 shrink-0" />
                                  <span className="truncate">{display.name}</span>
                                </a>
                                <button
                                  type="button"
                                  onClick={() => window.open(display.url, "_blank")}
                                  className="text-sky-600 hover:text-sky-800"
                                  title="View"
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm">Not uploaded</span>
                            );
                          })()
                        ) : (
                          <span className="block truncate" title={String(rec[col.key] ?? "")}>
                            {rec[col.key] || "—"}
                          </span>
                        )}
                      </td>
                    ))}
                    <td className="py-3 px-2 sm:px-4">
                      <div className="flex justify-center items-center space-x-2 text-gray-600">
                        <Edit2
                          className="h-4 w-4 cursor-pointer hover:text-sky-600"
                          onClick={() => {
                            setApiError("");
                            setSelected(isApiBacked ? { ...rec, resume: rec.resume } : rec);
                            setIsEditMode(true);
                          }}
                        />
                        <Eye
                          className="h-4 w-4 cursor-pointer hover:text-sky-600"
                          onClick={() => {
                            setSelected(isApiBacked ? { ...rec, resume: rec.resume } : rec);
                            setIsEditMode(false);
                          }}
                        />
                       
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={totalColumns} className="text-center py-4 text-gray-500">
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-3 text-sm">
          <div className="text-gray-500">
            Rows per page:
            <select
              className="border border-gray-300 rounded-md px-2 py-1 ml-1"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              {[5, 10, 15].map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center flex-wrap justify-center gap-1">
            <button
              onClick={() => goToPage(1)}
              disabled={currentPage === 1}
              className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronsLeft size={20} />
            </button>
            <button
              className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={20} />
            </button>

            {(() => {
              let startPage = Math.max(currentPage - 1, 1);
              let endPage = startPage + 2;
              if (endPage > totalPages) {
                endPage = totalPages;
                startPage = Math.max(endPage - 2, 1);
              }
              const pages = [];
              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <button
                    key={i}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      i === currentPage
                        ? "text-white bg-[linear-gradient(135deg,#2563eb,#d12465)]"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                    onClick={() => goToPage(i)}
                  >
                    {i}
                  </button>
                );
              }
              return pages;
            })()}

            <button
              className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <ChevronRight size={20} />
            </button>
            <button
              onClick={() => goToPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronsRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {showAddModal && (
        <AddModal
          moduleDef={moduleDef}
          onClose={() => setShowAddModal(false)}
          onSave={handleAdd}
          allData={allData}
          externalError={isApiBacked ? apiError : ""}
          submitting={isApiBacked ? submitting : false}
        />
      )}

      {selected && (
        <ViewEditModal
          moduleDef={moduleDef}
          record={selected}
          isEditMode={isEditMode}
          onClose={() => setSelected(null)}
          onSave={handleSaveEdit}
          allData={allData}
          externalError={isApiBacked ? apiError : ""}
          submitting={isApiBacked ? submitting : false}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main page: tab bar + active module table
// 🔌 CONNECT: Candidates, Interviews, Onboarding, Offboarding, Documents and
// Clearance are all backed by their live contexts now.
// ---------------------------------------------------------------------------
export default function RecruitmentManagement() {
  const [activeTab, setActiveTab] = useState("candidates");
  const { resumes } = useResumes();
  const { interviews } = useInterviews();
  const { onboardings } = useOnboardings();
  const { offboardings } = useOffboardings();
  const { recruitmentDocuments } = useRecruitmentDocuments();
  const { clearances } = useClearances();
  const { departments } = useDepartments();
  const { employees } = useEmployees();
  const employeeOptions = employees.map((e) => ({
    id: e.empId,
    name: `${e.first_name} ${e.last_name}`.trim(),
    department: e.department,
  }));
  const [dataByModule, setDataByModule] = useState(() =>
    Object.fromEntries(Object.entries(MODULES).map(([key, def]) => [key, def.seed]))
  );

  const setRecordsFor = (key) => (updater) =>
    setDataByModule((prev) => ({
      ...prev,
      [key]: typeof updater === "function" ? updater(prev[key]) : updater,
    }));

  const activeModuleDef = MODULES[activeTab];

  return (
    <div className="bg-sky-50 min-h-screen">
      <div className="px-4 sm:px-6 md:px-9 py-5">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-1">
          Recruitment Management
        </h1>
        <p className="text-sm text-gray-500 mb-5">
          Track candidates from resume to offboarding — interviews, onboarding, exit and document sharing, all in one place.
        </p>

        {/* Tab bar */}
        <div className="bg-white rounded-xl shadow-sm p-2 mb-6 overflow-x-auto">
          <div className="flex gap-1 min-w-max">
            {Object.entries(MODULES).map(([key, def]) => {
              const Icon = def.icon;
              const isActive = key === activeTab;
              const count =
                key === "candidates"
                  ? resumes.length
                  : key === "interviews"
                  ? interviews.length
                  : key === "onboarding"
                  ? onboardings.length
                  : key === "offboarding"
                  ? offboardings.length
                  : key === "documents"
                  ? recruitmentDocuments.length
                  : key === "clearance"
                  ? clearances.length
                  : dataByModule[key]?.length ?? 0;
              return (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? "text-white"
                      : "text-gray-600 hover:bg-sky-50"
                  }`}
                  style={isActive ? { background: GRADIENT_BTN } : {}}
                >
                  <Icon className="h-4 w-4" />
                  {def.label}
                  <span
                    className={`ml-1 text-sm rounded-full px-1.5 ${
                      isActive ? "bg-white/20" : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <ModuleTable
          key={activeTab}
          moduleKey={activeTab}
          moduleDef={activeModuleDef}
          records={dataByModule[activeTab]}
          setRecords={setRecordsFor(activeTab)}
          allData={{
            ...dataByModule,
            candidates: resumes,
            interviews: interviews,
            onboarding: onboardings,
            offboarding: offboardings,
            documents: recruitmentDocuments,
            clearance: clearances,
            departments: departments,
            employees: employeeOptions,
          }}
        />
      </div>
    </div>
  );
}